import React from 'react';
import EbookShell from '../components/EbookShell';

const RVBuyingGuideEbook = () => (
  <EbookShell ebookType="rv-buying-guide" variant="buying" testIdPrefix="buying-guide">
    {/* Cover */}
    <section className="page cover" data-testid="buying-guide-cover">
      <div className="ai-glow" />
      <div className="brand">RV Match A.I.</div>
      <h1>The Smart Buyer&rsquo;s RV Buying Guide</h1>
      <p className="subhead">Buy the right RV at the right price. Avoid the traps. Walk in confident, walk out paid.</p>
      <div className="cta">RVMatchAI.app</div>
      <div className="dollar-grid" />
      <div className="footer"><span>Buying Guide</span><span>Powered by RV Match A.I.</span></div>
    </section>

    <section className="page">
      <div className="brand">Chapter One</div>
      <h2>Know What You&rsquo;re Really Buying</h2>
      <p>Every RV category solves a different lifestyle problem. Pick the wrong category and the rest doesn&rsquo;t matter.</p>
      <div className="card-grid">
        <div className="card"><h3>Class A Motorhome</h3><p>Bus-style. Most space &amp; luxury. Higher fuel cost. Best for full-time, long-haul travelers.</p></div>
        <div className="card"><h3>Class B Camper Van</h3><p>Drivable. Park-anywhere agility. Compact. Great for solo travelers and weekend warriors.</p></div>
        <div className="card"><h3>Class C Motorhome</h3><p>Family-friendly cab-over bunk. Mid-size. Easier to drive than Class A, more room than Class B.</p></div>
        <div className="card"><h3>Travel Trailer</h3><p>Most popular. Wide range of sizes. Requires capable tow vehicle. Best long-term value.</p></div>
        <div className="card"><h3>Fifth Wheel</h3><p>Hauled by truck bed hitch. Spacious, residential layouts. Best for full-time families.</p></div>
        <div className="card"><h3>Toy Hauler</h3><p>Garage in the back. ATVs, motorcycles, gear. Doubles as patio at camp.</p></div>
      </div>
      <div className="footer"><span>01</span><span>Match the RV to the lifestyle</span></div>
    </section>

    <section className="page">
      <div className="brand">RV Match A.I.</div>
      <h2>Stop Guessing. Start Matching.</h2>
      <div className="image-box buying">Smart RV matching for serious buyers</div>
      <p>RV Match A.I. compares your budget, towing setup, family size, and travel goals against thousands of RVs to surface the ones that actually fit.</p>
      <p>Less doom-scrolling. Less sales pressure. More confidence.</p>
      <div className="footer"><span>02</span><span>RVMatchAI.app</span></div>
    </section>

    <section className="page">
      <div className="brand">Chapter Two</div>
      <h2>The True Cost of Ownership</h2>
      <p>The sticker is just the beginning. Smart buyers budget for the full picture before they sign.</p>
      <div className="card-grid">
        <div className="card"><h3>Insurance</h3><p>Plan $800&ndash;$2,000/yr depending on RV class &amp; coverage. Get a quote before purchase.</p></div>
        <div className="card"><h3>Storage</h3><p>$50&ndash;$300/mo if you can&rsquo;t park it at home. Indoor &amp; covered cost more.</p></div>
        <div className="card"><h3>Maintenance</h3><p>Roof reseal, tires, batteries, slide-out service. Budget 1&ndash;2% of value/yr.</p></div>
        <div className="card"><h3>Depreciation</h3><p>15&ndash;30% in year one. Buying lightly used is the single best money move.</p></div>
        <div className="card"><h3>Fuel &amp; Travel</h3><p>Class A: 7&ndash;10 MPG. Towables vary. Plan campgrounds, gas, and tolls realistically.</p></div>
        <div className="card"><h3>Campground Fees</h3><p>$30&ndash;$100/night. Memberships (Thousand Trails, Harvest Hosts) cut this fast.</p></div>
      </div>
      <div className="footer"><span>03</span><span>Budget the boring stuff</span></div>
    </section>

    <section className="page">
      <div className="brand">Chapter Three</div>
      <h2>Financing Without Getting Cooked</h2>
      <ul>
        <li><strong>Get pre-approved through your own bank or credit union first.</strong> Sales offices will try to flip you to a higher-rate lender.</li>
        <li><strong>Avoid 240-month loans.</strong> 20-year RV loans are predatory. RVs depreciate too fast to stay above water.</li>
        <li><strong>Don&rsquo;t finance the trade.</strong> Roll-overs bury equity and trap you in the next deal.</li>
        <li><strong>Ignore the &ldquo;monthly payment&rdquo; trick.</strong> Always negotiate the out-the-door price first, then talk financing.</li>
        <li><strong>Request the buyer&rsquo;s order before you sign anything.</strong> Every fee should be itemized.</li>
      </ul>
      <div className="footer"><span>04</span><span>Negotiate the price &mdash; not the payment</span></div>
    </section>

    <section className="page">
      <div className="brand">Chapter Four</div>
      <h2>Inspect Before You Sign</h2>
      <div className="image-box inspect">Walk slow. Look hard. Trust nothing.</div>
      <div className="card-grid">
        <div className="card"><h3>Roof &amp; Seals</h3><p>Soft spots, cracked sealant, water stains. Roof rebuilds run $5K&ndash;$15K.</p></div>
        <div className="card"><h3>Slides &amp; Awnings</h3><p>Cycle every slide and the awning. Listen for grinding. Look for tears or rust.</p></div>
        <div className="card"><h3>Tires &amp; Brakes</h3><p>Check date codes. RV tires age out at 5&ndash;7 years even with good tread.</p></div>
        <div className="card"><h3>Plumbing &amp; Tanks</h3><p>Run hot water. Test toilet flush. Sniff under the sink. Look for soft floor.</p></div>
        <div className="card"><h3>Electrical</h3><p>Plug in shore power. Run AC, microwave, fridge. Check inverter &amp; battery age.</p></div>
        <div className="card"><h3>Pro Inspection</h3><p>Pay $400&ndash;$800 for a NRVIA-certified inspector before any used purchase.</p></div>
      </div>
      <div className="footer"><span>05</span><span>One inspection saves ten regrets</span></div>
    </section>

    <section className="page">
      <div className="brand">Chapter Five</div>
      <h2>Negotiation Power Moves</h2>
      <ul>
        <li><strong>Anchor with comps.</strong> Pull 3 listings of the same year/make/model and lead with the lowest.</li>
        <li><strong>Walk away once.</strong> Sales reps respect buyers who can leave. The follow-up call usually drops the price.</li>
        <li><strong>End-of-month / end-of-quarter timing.</strong> Sales managers chase quotas. You catch the best deals near the deadline.</li>
        <li><strong>Don&rsquo;t fall for &ldquo;today only&rdquo;.</strong> The discount is real. The deadline isn&rsquo;t. Sleep on it.</li>
        <li><strong>Negotiate add-ons separately.</strong> Hitch install, propane fill, walk-through &mdash; these are often free or padded.</li>
        <li><strong>Read the contract twice.</strong> Strike anything you didn&rsquo;t agree to. If they push back, walk.</li>
      </ul>
      <div className="footer"><span>06</span><span>Prepared beats persuasive</span></div>
    </section>

    <section className="page">
      <div className="brand">Chapter Six</div>
      <h2>Trade-Ins &amp; Selling Your Current RV</h2>
      <p>Trades feel convenient. They also cost you money. Here&rsquo;s how to keep more of it.</p>
      <div className="card-grid">
        <div className="card"><h3>Never Reveal the Trade First</h3><p>Negotiate the new RV price to the floor, THEN mention a trade. Combining them lets the dealer hide margin.</p></div>
        <div className="card"><h3>Know Your Book Value</h3><p>Pull NADA + JD Power values. Take screenshots. Bring them in. Anchor the conversation with real numbers.</p></div>
        <div className="card"><h3>Detail It First</h3><p>A $150 detail + $50 in touch-ups adds $500&ndash;$1,500 in perceived value at appraisal.</p></div>
        <div className="card"><h3>Get 3 Written Offers</h3><p>Camping World, RV Trader instant offer, and one independent dealer. Best offer sets the floor for negotiation.</p></div>
        <div className="card"><h3>Consider Private Sale</h3><p>Facebook Marketplace + RV Trader listings usually net $2K&ndash;$8K more than trade-in. Worth the two weekends.</p></div>
        <div className="card"><h3>Tax Advantage Reality</h3><p>Some states discount sales tax on trade value. Do the math &mdash; sometimes a slightly lower trade still nets more after tax.</p></div>
      </div>
      <div className="footer"><span>07</span><span>The trade is a second negotiation</span></div>
    </section>

    <section className="page">
      <div className="brand">Chapter Seven</div>
      <h2>Insurance, Warranties &amp; Extended Coverage</h2>
      <ul>
        <li><strong>Full-timers need full-time coverage.</strong> Standard RV policies exclude damage while it&rsquo;s your primary residence.</li>
        <li><strong>Agreed value beats actual cash value.</strong> Pay slightly more up front; get a real payout after a loss.</li>
        <li><strong>Skip the F&amp;I office extended warranty.</strong> Independent RV warranty companies (Wholesale Warranties, XtraRide) often price 40&ndash;60% less for identical coverage.</li>
        <li><strong>Roof warranties are usually pro-rated garbage.</strong> Read the fine print. Most exclude everything you&rsquo;d actually file for.</li>
        <li><strong>Roadside assistance matters more than you think.</strong> Coach-Net or Good Sam ERS &mdash; not AAA, which won&rsquo;t tow an RV.</li>
        <li><strong>Ask for the underwriter, not the brand.</strong> Progressive vs. Foremost vs. National General all price wildly differently for the same rig.</li>
      </ul>
      <div className="footer"><span>08</span><span>Cheap coverage is expensive coverage</span></div>
    </section>

    <section className="page">
      <div className="brand">Chapter Eight</div>
      <h2>Your First 12 Months of Ownership</h2>
      <div className="card-grid">
        <div className="card"><h3>Month 1 &mdash; Shakedown</h3><p>Weekend trip within 50 miles. Something WILL leak, squeak, or fail. Bring the punch list back to the dealer under warranty.</p></div>
        <div className="card"><h3>Month 2&ndash;3 &mdash; The Fixes</h3><p>PDI list gets addressed. Track everything in writing. This is when dealers try to stall &mdash; be relentless.</p></div>
        <div className="card"><h3>Month 4&ndash;6 &mdash; The Upgrades</h3><p>Solar, lithium, water filtration, backup camera. Do the boring safety stuff BEFORE the fun stuff.</p></div>
        <div className="card"><h3>Month 6 &mdash; First Roof Check</h3><p>Climb up. Feel every seal. Reseal anything cracking. A tube of Dicor is $10. A soft floor is $8,000.</p></div>
        <div className="card"><h3>Month 9 &mdash; Winterize or Migrate</h3><p>Below-freezing coming? Blow the lines, pump antifreeze, pull batteries. Or drive south. Either works.</p></div>
        <div className="card"><h3>Month 12 &mdash; Full Service</h3><p>Wheel bearings, tire pressure/date check, brake inspection, sealant tour, appliance certifications.</p></div>
      </div>
      <div className="footer"><span>09</span><span>The first year sets the next ten</span></div>
    </section>

    <section className="page">
      <div className="brand">Chapter Nine</div>
      <h2>Buyer&rsquo;s Vocabulary Cheat Sheet</h2>
      <ul>
        <li><strong>MSRP:</strong> Manufacturer&rsquo;s Suggested Retail Price. Ignore it. Real transactions land 25&ndash;40% below MSRP.</li>
        <li><strong>GVWR / CCC:</strong> Gross Vehicle Weight Rating and Cargo Carrying Capacity. If CCC is under 1,500 lb, you&rsquo;ll be overweight the first week.</li>
        <li><strong>Dry Weight vs. Loaded Weight:</strong> Dry is the sticker lie. Loaded is real. Assume you&rsquo;ll be 800&ndash;1,500 lb heavier than dry.</li>
        <li><strong>Doc Fees / Prep Fees:</strong> Padding. Negotiable. Often 100% removable if you push.</li>
        <li><strong>Buyer&rsquo;s Order:</strong> The itemized offer sheet BEFORE the finance office. Demand it in writing before you agree to anything.</li>
        <li><strong>DP / TT / FW / TC:</strong> Diesel Pusher / Travel Trailer / Fifth Wheel / Truck Camper.</li>
        <li><strong>Boondocking:</strong> Camping without hookups. Solar + tanks + batteries make this possible for days at a time.</li>
        <li><strong>Winterizing:</strong> The process of blowing water out of lines + adding antifreeze so pipes don&rsquo;t split in winter.</li>
        <li><strong>Delamination:</strong> The outer wall separating from the frame. Usually water-damage related. Very expensive. Walk away.</li>
      </ul>
      <div className="footer"><span>10</span><span>Speak the language, spot the tricks</span></div>
    </section>

    <section className="page cover">
      <div className="ai-glow" />
      <div className="brand">RV Match A.I.</div>
      <h1>Buy Smart. Drive Confident.</h1>
      <p className="subhead">Your perfect RV is out there. Let AI match you to it.</p>
      <div className="cta">Start at RVMatchAI.app</div>
      <div className="dollar-grid" />
      <div className="footer"><span>Final Page</span><span>RVMatchAI.app</span></div>
    </section>
  </EbookShell>
);

export default RVBuyingGuideEbook;
